import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router-dom'
import { CornerUpLeft, Plus } from 'lucide-react'
import type { TaskDetail } from '@witty-board/shared'
import { useMeQuery } from '../../api/auth.js'
import { useCreateTaskMutation, useTaskDetailQuery } from '../../api/tasks.js'
import { QuickAddTask } from '../board/QuickAddTask.js'
import { taskPanelPath } from '../../lib/board-routes.js'

/** Enlace al padre si la tarea abierta es una hija (CLAUDE.md §6). Un solo nivel: nunca hay más de un padre. */
export function TaskParentLink({ parentId }: { parentId: string }) {
  const { t } = useTranslation()
  const { key = '', status } = useParams<{ key: string; status: string }>()
  const location = useLocation()
  const parent = useTaskDetailQuery(parentId)

  if (!parent.data) return null

  return (
    <Link
      to={{ pathname: taskPanelPath(key, parentId, status), search: location.search }}
      className="flex w-fit items-center gap-1 rounded-card px-2 py-1 text-meta text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <CornerUpLeft className="h-3 w-3" aria-hidden="true" />
      {t('task.detail.parentLink', { title: parent.data.title })}
    </Link>
  )
}

interface TaskSubtasksProps {
  task: TaskDetail
}

/** Lista de subtareas con alta rápida: sin esto la lista nunca se puede rellenar (ver docs/DECISIONS.md, fase 4). */
export function TaskSubtasks({ task }: TaskSubtasksProps) {
  const { t } = useTranslation()
  const { key = '', status } = useParams<{ key: string; status: string }>()
  const location = useLocation()
  const me = useMeQuery()
  const [adding, setAdding] = useState(false)
  const createTask = useCreateTaskMutation({ projectId: task.projectId }, me.data?.id ?? '')

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-meta font-medium text-text-muted">{t('task.detail.subtasksTitle')}</h2>
        <button
          type="button"
          onClick={() => setAdding(true)}
          aria-label={t('board.quickAdd.add')}
          className="flex h-6 w-6 max-md:h-11 max-md:w-11 items-center justify-center rounded-card text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex flex-col gap-1">
        {task.children.map((child) => (
          <Link
            key={child.id}
            to={{ pathname: taskPanelPath(key, child.id, status), search: location.search }}
            className="flex h-8 items-center justify-between gap-2 rounded-card px-2 text-body text-text hover:bg-surface-hi"
          >
            <span className="truncate">{child.title}</span>
            <span className="shrink-0 text-meta text-text-muted">
              {t(`board.columns.${child.status}`)}
            </span>
          </Link>
        ))}

        {adding && (
          <QuickAddTask
            onSubmit={(title) => {
              createTask.mutate({
                projectId: task.projectId,
                parentId: task.id,
                title,
                status: 'todo',
                priority: 'normal',
              })
              setAdding(false)
            }}
            onClose={() => setAdding(false)}
          />
        )}

        {task.children.length === 0 && !adding && (
          <p className="text-meta text-text-muted">{t('task.detail.noSubtasks')}</p>
        )}
      </div>
    </section>
  )
}
